import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Shield, 
  Users, 
  Radio, 
  Bell, 
  CreditCard,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Search,
  Edit2,
  Infinity,
  Crown,
  Activity,
  CheckCircle,
  XCircle,
  Play,
  RefreshCw,
  Clock,
  Trash2,
  Ban,
  X,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free', monitors: 2, alerts: 10 },
  { value: 'starter', label: 'Starter', monitors: 5, alerts: 200 },
  { value: 'pro', label: 'Pro', monitors: 20, alerts: 1000 },
  { value: 'enterprise', label: 'Enterprise', monitors: 100, alerts: 10000 },
  { value: 'unlimited', label: 'Unlimited', monitors: 999999, alerts: 999999 },
];

// Helper function to render service status badge
const ServiceStatusBadge = ({ monitoringStatus }) => {
  if (monitoringStatus?.service?.is_running) {
    return (
      <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
        <Activity className="w-3 h-3 mr-1 animate-pulse" />
        Running
      </Badge>
    );
  }
  if (monitoringStatus?.service?.last_run_success) {
    return (
      <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
        <CheckCircle className="w-3 h-3 mr-1" />
        Healthy
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20">
      <XCircle className="w-3 h-3 mr-1" />
      Error
    </Badge>
  );
};

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    plan: '',
    alerts_limit: '',
    monitors_limit: ''
  });
  const [saving, setSaving] = useState(false);
  const [monitoringStatus, setMonitoringStatus] = useState(null);
  const [triggeringMonitor, setTriggeringMonitor] = useState(false);
  const [drilldownModal, setDrilldownModal] = useState({ open: false, type: null, data: null, loading: false });
  const limit = 10;

  const fetchMonitoringStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/monitoring/status`, { withCredentials: true });
      setMonitoringStatus(res.data);
    } catch (error) {
      // Silent failure for non-admin or network errors
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchMonitoringStatus();
    // Refresh monitoring status every 30 seconds
    const interval = setInterval(fetchMonitoringStatus, 30000);
    return () => clearInterval(interval);
  }, [page, fetchMonitoringStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, {
          params: { skip: page * limit, limit },
          withCredentials: true
        })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setTotal(usersRes.data.total || 0);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      plan: user.plan || 'free',
      alerts_limit: user.usage_limit?.toString() || '10',
      monitors_limit: ''
    });
    setEditDialogOpen(true);
  };

  const handlePlanChange = (plan) => {
    const planInfo = PLAN_OPTIONS.find(p => p.value === plan);
    setEditForm({
      ...editForm,
      plan,
      alerts_limit: planInfo ? planInfo.alerts.toString() : editForm.alerts_limit,
      monitors_limit: planInfo ? planInfo.monitors.toString() : editForm.monitors_limit
    });
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/users/${selectedUser.user_id}`, {
        plan: editForm.plan,
        alerts_limit: parseInt(editForm.alerts_limit) || undefined,
        monitors_limit: parseInt(editForm.monitors_limit) || undefined
      }, { withCredentials: true });
      
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleGrantUnlimited = async (userId, userName) => {
    if (!confirm(`Grant unlimited plan to ${userName}?`)) return;
    
    try {
      await axios.post(`${API}/admin/users/${userId}/grant-unlimited`, {}, { withCredentials: true });
      toast.success(`Unlimited plan granted to ${userName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant unlimited plan');
    }
  };

  const handleBanUser = async (userId, userName, isBanned) => {
    const action = isBanned ? 'unban' : 'ban';
    if (!confirm(`Are you sure you want to ${action} ${userName}?`)) return;
    
    try {
      await axios.post(`${API}/admin/users/${userId}/ban`, {}, { withCredentials: true });
      toast.success(`User ${isBanned ? 'unbanned' : 'banned'} successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to PERMANENTLY DELETE ${userName} and all their data? This action cannot be undone.`)) return;
    if (!confirm(`This will delete ALL monitors, alerts, and settings for ${userName}. Type "delete" in the next prompt to confirm.`)) return;
    
    const confirmText = prompt('Type "delete" to confirm:');
    if (confirmText?.toLowerCase() !== 'delete') {
      toast.error('Deletion cancelled');
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success(`User ${userName} deleted successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleTriggerMonitoring = async () => {
    setTriggeringMonitor(true);
    try {
      await axios.post(`${API}/monitoring/trigger`, {}, { withCredentials: true });
      toast.success('Monitoring cycle triggered');
      // Refresh status after a short delay
      setTimeout(fetchMonitoringStatus, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger monitoring');
    } finally {
      setTriggeringMonitor(false);
    }
  };

  const handleTriggerTrialCheck = async () => {
    try {
      await axios.post(`${API}/monitoring/trigger-trial-check`, {}, { withCredentials: true });
      toast.success('Trial expiration check completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run trial check');
    }
  };

  const handleStatClick = async (statType) => {
    setDrilldownModal({ open: true, type: statType, data: null, loading: true });
    
    try {
      let endpoint = '';
      switch (statType) {
        case 'users':
          endpoint = '/admin/users?limit=100';
          break;
        case 'monitors':
          endpoint = '/admin/stats/monitors';
          break;
        case 'alerts':
          endpoint = '/admin/stats/alerts?limit=50';
          break;
        case 'subscriptions':
          endpoint = '/admin/stats/paid-subscribers';
          break;
        default:
          return;
      }
      
      const response = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      setDrilldownModal(prev => ({ ...prev, data: response.data, loading: false }));
    } catch (error) {
      toast.error('Failed to load details');
      setDrilldownModal({ open: false, type: null, data: null, loading: false });
    }
  };

  const closeDrilldown = () => {
    setDrilldownModal({ open: false, type: null, data: null, loading: false });
  };

  const filteredUsers = search
    ? users.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const totalPages = Math.ceil(total / limit);

  const statCards = [
    { title: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-primary', type: 'users' },
    { title: 'Total Monitors', value: stats?.total_monitors || 0, icon: Radio, color: 'text-accent', type: 'monitors' },
    { title: 'Total Alerts', value: stats?.total_alerts || 0, icon: Bell, color: 'text-success', type: 'alerts' },
    { title: 'Paid Subscriptions', value: stats?.active_subscriptions || 0, icon: CreditCard, color: 'text-purple-500', type: 'subscriptions' }
  ];

  const getPlanBadge = (plan) => {
    if (plan === 'unlimited') {
      return <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white"><Infinity className="w-3 h-3 mr-1" />Unlimited</Badge>;
    }
    return <Badge variant={plan === 'free' ? 'secondary' : 'default'} className="capitalize">{plan || 'free'}</Badge>;
  };

  if (loading && page === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight" data-testid="admin-page-title">
                Admin Panel
              </h1>
              <p className="text-muted-foreground">
                System overview and user management
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className="border-border/50 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all"
                  onClick={() => handleStatClick(stat.type)}
                  data-testid={`stat-card-${stat.type}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{stat.title}</p>
                        <p className="text-3xl font-bold font-['Outfit'] mt-1">{stat.value}</p>
                      </div>
                      <Icon className={`w-8 h-8 ${stat.color} opacity-50`} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Click to view details
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Monitoring Service Status */}
        {monitoringStatus && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="border-border/50">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Activity className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Background Monitoring Service</CardTitle>
                      <CardDescription>Real-time channel monitoring status</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchMonitoringStatus}
                      className="rounded-full"
                      data-testid="refresh-monitoring-status"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleTriggerMonitoring}
                      disabled={triggeringMonitor || monitoringStatus?.service?.is_running}
                      className="rounded-full"
                      data-testid="trigger-monitoring-btn"
                    >
                      {triggeringMonitor ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Run Now
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Service Status */}
                  <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <ServiceStatusBadge monitoringStatus={monitoringStatus} />
                    </div>
                    <p className="text-xs text-muted-foreground">Service Status</p>
                  </div>

                  {/* Last Run */}
                  <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {monitoringStatus.service?.last_run 
                          ? new Date(monitoringStatus.service.last_run).toLocaleTimeString()
                          : 'Never'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">Last Run</p>
                  </div>

                  {/* Channels Checked */}
                  <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Radio className="w-4 h-4 text-accent" />
                      <span className="text-sm font-medium">
                        {monitoringStatus.service?.channels_checked || 0} / {monitoringStatus.database_stats?.active_monitors || 0}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">Channels Checked (Last Cycle / Total Active)</p>
                  </div>

                  {/* Run Stats */}
                  <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-green-600">
                        {monitoringStatus.service?.successful_runs || 0}
                      </span>
                      <span className="text-xs text-muted-foreground">/</span>
                      <span className="text-sm font-medium text-red-600">
                        {monitoringStatus.service?.failed_runs || 0}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">Success / Failed Runs</p>
                  </div>
                </div>

                {/* Additional Stats Row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                  <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                    <p className="text-2xl font-bold font-['Outfit']">{monitoringStatus.database_stats?.active_telegram_sessions || 0}</p>
                    <p className="text-xs text-muted-foreground">Active Telegram Sessions</p>
                  </div>
                  <div className="p-4 rounded-lg bg-accent/5 border border-accent/10">
                    <p className="text-2xl font-bold font-['Outfit']">{monitoringStatus.database_stats?.alerts_today || 0}</p>
                    <p className="text-xs text-muted-foreground">Alerts Sent Today</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
                    <p className="text-2xl font-bold font-['Outfit']">{monitoringStatus.monitoring_interval_seconds || 60}s</p>
                    <p className="text-xs text-muted-foreground">Monitoring Interval</p>
                  </div>
                </div>

                {/* Last Error */}
                {monitoringStatus.service?.last_error && (
                  <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/20">
                    <p className="text-sm text-red-600 dark:text-red-400">
                      <strong>Last Error:</strong> {monitoringStatus.service.last_error}
                    </p>
                  </div>
                )}

                {/* Manual Actions */}
                <div className="mt-4 pt-4 border-t border-border/50">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm text-muted-foreground">Manual Actions:</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTriggerTrialCheck}
                      className="rounded-full"
                      data-testid="trigger-trial-check-btn"
                    >
                      Check Trial Expirations
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Users Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="border-border/50">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg">Users</CardTitle>
                  <CardDescription>Manage all registered users</CardDescription>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="admin-search"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Usage</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Joined</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.user_id} className={`border-b border-border/50 hover:bg-muted/30 ${user.is_banned ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${user.is_banned ? 'bg-red-100 text-red-600' : 'bg-primary/10'}`}>
                              {user.name?.charAt(0) || 'U'}
                            </div>
                            <span className={`font-medium text-sm ${user.is_banned ? 'line-through text-muted-foreground' : ''}`}>{user.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">{user.email}</td>
                        <td className="py-3 px-4">
                          {user.is_banned ? (
                            <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20">
                              <Ban className="w-3 h-3 mr-1" />
                              Banned
                            </Badge>
                          ) : (
                            <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Active
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {getPlanBadge(user.plan)}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {user.plan === 'unlimited' ? (
                            <span className="text-muted-foreground">∞</span>
                          ) : (
                            `${user.usage_this_month || 0} / ${user.usage_limit || 10}`
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(user)}
                              title="Edit user"
                              data-testid={`edit-user-${user.user_id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {user.plan !== 'unlimited' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleGrantUnlimited(user.user_id, user.name)}
                                className="text-yellow-600 hover:text-yellow-700"
                                title="Grant unlimited"
                                data-testid={`grant-unlimited-${user.user_id}`}
                              >
                                <Crown className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleBanUser(user.user_id, user.name, user.is_banned)}
                              className={user.is_banned ? "text-green-600 hover:text-green-700" : "text-orange-600 hover:text-orange-700"}
                              title={user.is_banned ? "Unban user" : "Ban user"}
                              data-testid={`ban-user-${user.user_id}`}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user.user_id, user.name)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete user"
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="rounded-full"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground px-4">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="rounded-full"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update plan and limits for {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={editForm.plan} onValueChange={handlePlanChange}>
                  <SelectTrigger data-testid="plan-select">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_OPTIONS.map((plan) => (
                      <SelectItem key={plan.value} value={plan.value}>
                        <div className="flex items-center gap-2">
                          {plan.value === 'unlimited' && <Infinity className="w-4 h-4 text-yellow-500" />}
                          {plan.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="alerts_limit">Alerts Limit (per month)</Label>
                <Input
                  id="alerts_limit"
                  type="number"
                  value={editForm.alerts_limit}
                  onChange={(e) => setEditForm({ ...editForm, alerts_limit: e.target.value })}
                  placeholder="e.g., 100"
                  data-testid="alerts-limit-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="monitors_limit">Monitors Limit</Label>
                <Input
                  id="monitors_limit"
                  type="number"
                  value={editForm.monitors_limit}
                  onChange={(e) => setEditForm({ ...editForm, monitors_limit: e.target.value })}
                  placeholder="e.g., 10"
                  data-testid="monitors-limit-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-full">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveUser} 
                disabled={saving}
                className="rounded-full"
                data-testid="save-user-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Drill-down Modal */}
        <Dialog open={drilldownModal.open} onOpenChange={closeDrilldown}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {drilldownModal.type === 'users' && <><Users className="w-5 h-5" /> All Users</>}
                {drilldownModal.type === 'monitors' && <><Radio className="w-5 h-5" /> All Monitors</>}
                {drilldownModal.type === 'alerts' && <><Bell className="w-5 h-5" /> Recent Alerts</>}
                {drilldownModal.type === 'subscriptions' && <><CreditCard className="w-5 h-5" /> Paid Subscribers</>}
              </DialogTitle>
              <DialogDescription>
                {drilldownModal.data && `${drilldownModal.data.total || 0} total records`}
              </DialogDescription>
            </DialogHeader>

            {drilldownModal.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : drilldownModal.data && (
              <div className="space-y-4">
                {/* Users Drill-down */}
                {drilldownModal.type === 'users' && drilldownModal.data.users && (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium">Name</th>
                          <th className="text-left py-2 px-3 font-medium">Email</th>
                          <th className="text-left py-2 px-3 font-medium">Plan</th>
                          <th className="text-left py-2 px-3 font-medium">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drilldownModal.data.users.map((user, i) => (
                          <tr key={user.user_id || i} className="border-t border-border/50">
                            <td className="py-2 px-3">{user.name || 'N/A'}</td>
                            <td className="py-2 px-3 text-muted-foreground">{user.email}</td>
                            <td className="py-2 px-3">{getPlanBadge(user.plan)}</td>
                            <td className="py-2 px-3 text-muted-foreground">{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</td>
                          </tr>
                        ))}
                        {drilldownModal.data.users.length === 0 && (
                          <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">No users found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Monitors Drill-down */}
                {drilldownModal.type === 'monitors' && drilldownModal.data.monitors && (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium">Channel</th>
                          <th className="text-left py-2 px-3 font-medium">User</th>
                          <th className="text-left py-2 px-3 font-medium">Status</th>
                          <th className="text-left py-2 px-3 font-medium">Last Check</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drilldownModal.data.monitors.map((monitor, i) => (
                          <tr key={monitor.monitor_id || i} className="border-t border-border/50">
                            <td className="py-2 px-3 font-medium">{monitor.channel_title || monitor.channel_username || 'Unknown'}</td>
                            <td className="py-2 px-3 text-muted-foreground">{monitor.user_email}</td>
                            <td className="py-2 px-3">
                              <Badge variant={monitor.active ? 'default' : 'secondary'}>
                                {monitor.active ? 'Active' : 'Paused'}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-muted-foreground">{monitor.last_check ? new Date(monitor.last_check).toLocaleString() : 'Never'}</td>
                          </tr>
                        ))}
                        {drilldownModal.data.monitors.length === 0 && (
                          <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">No monitors found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Alerts Drill-down */}
                {drilldownModal.type === 'alerts' && drilldownModal.data.alerts && (
                  <div className="space-y-3">
                    {drilldownModal.data.alerts.map((alert, i) => (
                      <div key={alert.alert_id || i} className="p-3 rounded-lg border border-border/50 bg-muted/30">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{alert.channel_title || 'Unknown Channel'}</p>
                            <p className="text-xs text-muted-foreground">{alert.user_email}</p>
                            <p className="text-sm mt-1 line-clamp-2">{alert.message_text}</p>
                            {alert.matched_keywords?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {alert.matched_keywords.map((kw, ki) => (
                                  <Badge key={ki} variant="secondary" className="text-xs">{kw}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {alert.created_at ? new Date(alert.created_at).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                    ))}
                    {drilldownModal.data.alerts.length === 0 && (
                      <div className="py-8 text-center text-muted-foreground">No alerts found</div>
                    )}
                  </div>
                )}

                {/* Paid Subscribers Drill-down */}
                {drilldownModal.type === 'subscriptions' && drilldownModal.data.subscribers && (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left py-2 px-3 font-medium">Name</th>
                          <th className="text-left py-2 px-3 font-medium">Email</th>
                          <th className="text-left py-2 px-3 font-medium">Plan</th>
                          <th className="text-left py-2 px-3 font-medium">Limits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drilldownModal.data.subscribers.map((sub, i) => (
                          <tr key={sub.user_id || i} className="border-t border-border/50">
                            <td className="py-2 px-3">{sub.name || 'N/A'}</td>
                            <td className="py-2 px-3 text-muted-foreground">{sub.email}</td>
                            <td className="py-2 px-3">{getPlanBadge(sub.plan)}</td>
                            <td className="py-2 px-3 text-muted-foreground text-xs">
                              {sub.subscription ? `${sub.subscription.alerts_limit || '?'} alerts, ${sub.subscription.monitors_limit || '?'} monitors` : 'N/A'}
                            </td>
                          </tr>
                        ))}
                        {drilldownModal.data.subscribers.length === 0 && (
                          <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">No paid subscribers yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeDrilldown} className="rounded-full">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
